import { z } from 'zod'

/**
 * ══ PHÂN TRANG — KEYSET, CÓ TRẦN CỨNG (KIEN-TRUC §8.3) ════════════════════════════════════════
 *
 * Trước đợt N2, **không endpoint danh sách nào phân trang**: `listCustomers` trả nguyên bảng.
 * Lỗi này không biểu hiện trên dữ liệu seed và biểu hiện đúng vào ngày chạy thật, khi bảng khách
 * hàng đã có vài nghìn dòng và mỗi lần mở màn Danh mục là một lần bốc cả bảng qua mạng.
 *
 * ⚠ KEYSET, KHÔNG PHẢI `OFFSET`. Hai lý do, lý do thứ hai mới là lý do thật:
 *   ① `OFFSET 10000` bắt Postgres đọc rồi vứt 10.000 dòng — chậm dần tuyến tính theo số trang.
 *   ② `OFFSET` **BỎ SÓT DÒNG** khi có ghi xen giữa hai lần lấy trang. Người dùng cuộn qua trang 2
 *      trong lúc đồng nghiệp thêm một khách hàng đứng trước theo thứ tự sắp xếp: mọi dòng dịch
 *      xuống một bậc, và dòng cuối của trang 1 không bao giờ xuất hiện lại. Trong một danh mục kế
 *      toán, "có dòng không bao giờ nhìn thấy" là lỗi im lặng tệ nhất.
 *
 * ⚠ TRẦN CỨNG 200. Không có trần thì `?limit=1000000` là một cửa vét sạch bảng hợp lệ về mặt kỹ
 *   thuật — đúng thứ mà phân trang sinh ra để chặn.
 */

export const PAGE_LIMIT_DEFAULT = 50
export const PAGE_LIMIT_MAX = 200

/**
 * Tham số truy vấn chung. `limit` đến từ chuỗi truy vấn nên phải ép kiểu, nhưng ép có kiểm: chuỗi
 * không phải số phải BÁO LỖI chứ không lặng lẽ rơi về mặc định — rơi về mặc định là người gọi tin
 * mình đang lấy 500 dòng trong khi thực tế nhận 50, và phần thiếu không hề báo.
 */
export const PageQuery = z.object({
  limit: z.coerce
    .number({ invalid_type_error: '`limit` phải là số.' })
    .int({ message: '`limit` phải là số nguyên.' })
    .min(1, { message: '`limit` tối thiểu là 1.' })
    .max(PAGE_LIMIT_MAX, { message: `\`limit\` tối đa là ${PAGE_LIMIT_MAX}.` })
    .default(PAGE_LIMIT_DEFAULT),
  /** Con trỏ MỜ — chuỗi đã mã hoá, KHÔNG phải id trần. Xem `@rs/db/keyset`. */
  cursor: z.string().min(1).optional(),
  /**
   * Hướng đi của cửa sổ — thanh phân trang bản cũ có BỐN nút, không phải hai:
   *   `next` (mặc định) « đầu (không con trỏ) và › sau (có con trỏ)
   *   `prev`  ‹ trước — con trỏ là dòng ĐẦU trang đang xem, đọc ngược rồi đảo lại
   *   `last`  » cuối  — bỏ qua con trỏ, lấy `limit` dòng cuối bảng
   * Vẫn là keyset: KHÔNG có `OFFSET` ở nhánh nào.
   */
  dir: z.enum(['next', 'prev', 'last']).default('next'),
})
export type PageQuery = z.infer<typeof PageQuery>

/** Bọc một lược đồ dòng thành lược đồ trang. */
export const pageOf = <T extends z.ZodTypeAny>(row: T): z.ZodObject<{
  rows: z.ZodArray<T>
  nextCursor: z.ZodNullable<z.ZodString>
  prevCursor: z.ZodOptional<z.ZodNullable<z.ZodString>>
  total: z.ZodOptional<z.ZodNullable<z.ZodNumber>>
}> => z.object({
  rows: z.array(row),
  /** `null` nghĩa là hết dữ liệu. KHÔNG dùng chuỗi rỗng — rỗng là một giá trị hợp lệ mơ hồ. */
  nextCursor: z.string().nullable(),
  /** Con trỏ lùi — `null` là đang ở trang đầu. Chỉ cửa đã chuyển sang cửa sổ hai chiều mới có. */
  prevCursor: z.string().nullable().optional(),
  /**
   * Tổng số dòng của TOÀN BỘ tập đang lọc (không phải của trang này). Thanh phân trang bản cũ hiện
   * "Hiện 21–40 / 137" và "Trang 2/7" — cả hai con số đó đều cần nó.
   *
   * ⚠ TUỲ CHỌN có chủ đích: ~50 cửa danh sách chưa chuyển vẫn trả đúng hai trường cũ và vẫn hợp
   *   lệ. Thân trả về KHÔNG bị kiểm bằng Zod lúc chạy (`http/validate.ts` chỉ kiểm
   *   querystring/body/params), nên đây thuần tuý là hợp đồng để đọc và để sinh OpenAPI.
   */
  total: z.number().int().nullable().optional(),
})

export interface Page<T> {
  rows: T[]
  nextCursor: string | null
  prevCursor?: string | null
  total?: number | null
}

/**
 * Ô 🔍 của một màn danh sách — lọc theo chuỗi gõ vào, CHẠY Ở MÁY CHỦ.
 *
 * ⚠ `trim()` trước khi kiểm độ dài: người dùng dán một khoảng trắng vào ô tìm kiếm thì đó là
 *   "không lọc", không phải "lọc theo dấu cách" — dạng sau trả về bảng rỗng và nhìn như mất dữ liệu.
 */
export const SearchQuery = z.object({ q: z.string().trim().min(1).optional() })
